import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { clearAuthToken, setAuthToken } from '../utils/authToken';
import logger from '../utils/logger';
import type { User } from '../types';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Auth Strategy (Cookie-First with Token Backup):
 *
 * 1. HTTP-only cookies are the PRIMARY auth mechanism (set by backend)
 * 2. In-memory tokens are BACKUP for edge cases (proxies stripping cookies, strict CORS)
 *
 * The backend checks cookies first, then falls back to Authorization header.
 */

interface AuthContextValue {
  user: User | null;
  setUser: (u: User | null) => void;
  loading: boolean;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  exchangeSession: (sessionId: string) => Promise<User>;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  canManageUsers: boolean;
  isPending: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Cookies are sent automatically; token backup goes via axios interceptor
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const exchangeSession = useCallback(async (sessionId: string): Promise<User> => {
    // Exchange Emergent Auth session_id for a session cookie + user data
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    const data = response.data;
    if (data?.session_token) setAuthToken(data.session_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Backend will clear the HTTP-only cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      logger.error('Logout error:', error);
    }
    clearAuthToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      loading,
      logout,
      checkAuth,
      exchangeSession,
      isAuthenticated: !!user,
      isSuperAdmin: user?.role === 'superadmin',
      isAdmin: ['superadmin', 'admin'].includes(user?.role as string),
      canEdit: ['superadmin', 'admin', 'warehouse', 'sales'].includes(user?.role as string),
      canManageUsers: ['superadmin', 'admin'].includes(user?.role as string),
      isPending: user?.role === 'pending'
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext) as AuthContextValue;
